import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Create the API slice
export const taskApi = createApi({
  reducerPath: 'taskApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Tasks', 'Task', 'TaskTemplates'],
  endpoints: (builder) => ({
    // Get all tasks
    getTasks: builder.query({
      query: (params = {}) => {
        const queryString = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryString.append(key, value);
          }
        });
        return `/tasks?${queryString.toString()}`;
      },
      providesTags: ['Tasks'],
    }),

    // Get single task
    getTask: builder.query({
      query: (taskId) => `/tasks/${taskId}`,
      providesTags: (result, error, taskId) => [{ type: 'Task', id: taskId }],
    }),

    // Create task
    createTask: builder.mutation({
      query: (taskData) => ({
        url: '/tasks',
        method: 'POST',
        body: taskData,
      }),
      invalidatesTags: ['Tasks'],
    }),

    // Update task
    updateTask: builder.mutation({
      query: ({ taskId, updates }) => ({
        url: `/tasks/${taskId}`,
        method: 'PUT',
        body: updates,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        'Tasks',
        { type: 'Task', id: taskId }
      ],
    }),

    // Delete task
    deleteTask: builder.mutation({
      query: (taskId) => ({
        url: `/tasks/${taskId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tasks'],
    }),

    // Add comment to task
    addComment: builder.mutation({
      query: ({ taskId, comment }) => ({
        url: `/tasks/${taskId}/comments`,
        method: 'POST',
        body: { message: comment },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId }
      ],
    }),

    // Update comment
    updateComment: builder.mutation({
      query: ({ taskId, commentId, message }) => ({
        url: `/tasks/${taskId}/comments/${commentId}`,
        method: 'PUT',
        body: { message },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId }
      ],
    }),

    // Delete comment
    deleteComment: builder.mutation({
      query: ({ taskId, commentId }) => ({
        url: `/tasks/${taskId}/comments/${commentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId }
      ],
    }),

    // Get task templates
    getTemplates: builder.query({
      query: () => '/tasks/templates',
      providesTags: ['TaskTemplates'],
    }),

    // Create task from template
    createFromTemplate: builder.mutation({
      query: ({ templateId, overrides }) => ({
        url: `/tasks/from-template/${templateId}`,
        method: 'POST',
        body: { overrides },
      }),
      invalidatesTags: ['Tasks'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useAddCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
  useGetTemplatesQuery,
  useCreateFromTemplateMutation,
} = taskApi;

// For backward compatibility with existing code that might use named exports
export default {
  getTasks: async (params = {}) => {
    const queryString = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryString.append(key, value);
      }
    });

    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks?${queryString.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch tasks');
    }

    return response.json();
  },

  getTask: async (taskId) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch task');
    }

    return response.json();
  },

  createTask: async (taskData) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(taskData),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create task');
    }

    return response.json();
  },

  updateTask: async (taskId, updates) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(updates),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to update task');
    }

    return response.json();
  },

  deleteTask: async (taskId) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to delete task');
    }

    return response.json();
  },

  addComment: async (taskId, comment) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/${taskId}/comments`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ message: comment }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to add comment');
    }

    return response.json();
  },

  getTemplates: async () => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/templates`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch templates');
    }

    return response.json();
  },

  createFromTemplate: async (templateId, overrides) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tasks/from-template/${templateId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ overrides }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to create task from template');
    }

    return response.json();
  },
};
